import moment from 'moment';
import { useMemo } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import { Button } from '@hivebrite/design-system/Button';

import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';

import MemberRequestForm from './MemberRequestForm';
import SuccessMessageJoinPublicTopic from '../TopicActions/SuccessMessageJoinPublicTopic';

const MemberRequestModal = ({
  forceDefaultLink,
  handleCancel,
  handleEdit,
  handleSubmitRequest,
  isCanceling,
  isEditing,
  isLoading,
  isSubmitting,
  requestApproval,
  href,
  setDisplayRequestModal,
  target,
  topic,
}) => {
  const hasCustomAttrs = useMemo(
    () => 'custom_attributes' in requestApproval,
    [requestApproval],
  );

  const hasAnyRequiredCustomAttrs = useMemo(() => {
    if ('custom_attributes' in requestApproval) {
      const customAttributes = requestApproval.custom_attributes;

      return (
        customAttributes.length > 0 &&
        customAttributes.some((customAttribute) => customAttribute.required)
      );
    }

    return false;
  }, [requestApproval]);

  const requestIsReady = useMemo(
    () => !!requestApproval.created_at,
    [requestApproval],
  );

  const shouldShowSuccessDirectly = useMemo(
    () =>
      (!!requestApproval.created_at &&
        !!requestApproval.auto_approval_user_list_id) ||
      requestApproval.status === 'approved',
    [requestApproval],
  );

  const shouldFillMessageToJoin = useMemo(() => {
    if (isEditing) {
      return true;
    }

    return !topic.approval_id;
  }, [isEditing, topic]);

  moment.locale(window.__HB_LOCALE__);

  const content = useMemo(
    () =>
      requestIsReady &&
      !shouldShowSuccessDirectly &&
      !shouldFillMessageToJoin ? (
        <Box flexDirection="column" flex={1}>
          <Box>{s__('Topics|Your request is being processed')}</Box>
          <Box>
            {I18n.t('web.topics.member_requests.requested_at', {
              date: moment(requestApproval.created_at).format('lll'),
            })}
          </Box>
          <Box justifyContent="flex-end" mt={8}>
            <Button
              mr={4}
              variant="secondary"
              isLoading={isCanceling}
              onClick={handleCancel}
            >
              {__('Cancel the request')}
            </Button>
            <Button type="submit" onClick={handleEdit}>
              {__('Edit the request')}
            </Button>
          </Box>
        </Box>
      ) : (
        ((requestIsReady && shouldFillMessageToJoin) || hasCustomAttrs) && (
          <MemberRequestForm
            handleSubmitRequest={handleSubmitRequest}
            isSubmitting={isSubmitting}
            onCancel={() => {
              handleCancel();
              setDisplayRequestModal(false);
            }}
            requestApproval={requestApproval}
          />
        )
      ),
    [
      requestIsReady,
      requestApproval,
      isCanceling,
      shouldShowSuccessDirectly,
      shouldFillMessageToJoin,
    ],
  );

  if (shouldShowSuccessDirectly && !hasAnyRequiredCustomAttrs) {
    return (
      <SuccessMessageJoinPublicTopic
        displaySuccessMessage={{
          forceDefaultLink,
          href,
          isDisplayed: true,
          name: topic.name,
          target,
        }}
        setDisplaySuccessMessage={() => setDisplayRequestModal(false)}
      />
    );
  }

  return (
    <ModalWithTemplate
      dataTestId="topic-member-request-modal"
      onCloseModal={() => {
        // When there is a new request to join
        if (!topic.approval_id) {
          // We delete the created approval and
          // do not display the success notification message
          handleCancel(false);
        }

        setDisplayRequestModal(false);
      }}
      header={s__('Topics|Request to join · %{topic}').i__({
        topic: topic.name,
      })}
      screenReadersCloseButtonText={s__(
        'TopicsAccessibility|Close request to join topic',
      )}
    >
      {isLoading ? <Loader size={50} /> : content}
    </ModalWithTemplate>
  );
};

export default MemberRequestModal;
